// lingland-frontend/src/pages/BlogPostPage.js
import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import SchemaMarkup from '../components/SchemaMarkup';
import useIntersectionObserver from '../hooks/useIntersectionObserver';
import { useBlogPost, useBlogPosts } from '../hooks/useBlogPosts';
import { Tag, Clock, ChevronLeft } from 'lucide-react';
import { BlogPostCard } from './BlogPostCard';
import './BlogPostPage.css';

function BlogPostPage() {
    const { slug } = useParams();
    const { t } = useTranslation(['translation', 'blog']);
    const navigate = useNavigate();
    const { post, loading, error } = useBlogPost(slug);
    const { posts: allPosts } = useBlogPosts();
    const [headerRef, isHeaderVisible] = useIntersectionObserver();
    const [contentRef, isContentVisible] = useIntersectionObserver();
    const [relatedRef, isRelatedVisible] = useIntersectionObserver();

    // Pobierz podobne posty (3 ostatnie oprócz bieżącego)
    const relatedPosts = allPosts
        .filter(p => p.slug !== slug)
        .slice(0, 3);

    if (loading) {
        return (
            <div className="container page-loading">
                <p>{t('page_loading', { ns: 'blog' })}</p>
            </div>
        );
    }

    if (error || !post) {
        return (
            <>
                <SEO 
                    title={t('article_not_found_title', { ns: 'blog' })} 
                    description={t('article_not_found_desc', { slug }, { ns: 'blog' })} 
                />
                <div className="container post-not-found">
                    <h2>{t('article_not_found_title', { ns: 'blog' })}</h2>
                    <p>{error || t('article_not_found_desc', { slug }, { ns: 'blog' })}</p>
                    <Link to="/blog" className="button-secondary">
                        {t('back_to_blog', { ns: 'blog' })}
                    </Link>
                </div>
            </>
        );
    }

    const readingTime = 5; // Simplified reading time calculation

    return (
        <>
            <SEO 
                title={post.seo?.title || post.title} 
                description={post.seo?.description || post.excerpt} 
                url={`/blog/${post.slug}`} 
                type="article" 
                imageUrl={post.image}
            />
            <SchemaMarkup 
                type="Article" 
                data={{
                    title: post.title,
                    excerpt: post.excerpt,
                    image: post.image,
                    slug: post.slug,
                    datePublished: post.published_at,
                    dateModified: post.published_at,
                    keywords: post.categories?.map(cat => cat.name).join(', ') || 'tłumaczenia, lokalizacja'
                }}
            />
            <SchemaMarkup 
                type="BreadcrumbList" 
                data={{
                    breadcrumbs: [
                        { name: 'Strona główna', url: '/' },
                        { name: 'Blog', url: '/blog' },
                        { name: post.title, url: `/blog/${post.slug}` }
                    ]
                }}
            />
            <article className="blog-post-page">
                <header ref={headerRef} className={`blog-post-header section-bg-light-gray animate-on-scroll ${isHeaderVisible ? 'is-visible' : ''}`}>
                    <div className="container">
                        <div className="post-meta-header">
                            <button onClick={() => navigate(-1)} className="back-to-blog-link">
                                <ChevronLeft size={16}/> {t('back_to_blog', { ns: 'blog' })}
                            </button>
                            {post.categories && post.categories.length > 0 && (
                                <span className="post-category-header">
                                    <Tag size={14}/> {post.categories[0].name}
                                </span>
                            )}
                        </div>
                        <h1>{post.title}</h1>
                        <div className="post-meta-subheader">
                            <span className="post-date">
                                <Clock size={16}/> {t('published_on', { ns: 'blog' })} {post.date}
                            </span>
                            <span className="reading-time">
                                <Clock size={16}/> {t('reading_time', { ns: 'blog', time: readingTime })}
                            </span>
                        </div>
                    </div>
                </header>

                <section ref={contentRef} className={`blog-post-content section-bg-white animate-on-scroll ${isContentVisible ? 'is-visible' : ''}`}>
                    <div className="container">
                        <div className="post-content">
                            {post.content ? (
                                <div>{post.content}</div>
                            ) : (
                                <p>{t('content_in_preparation', { ns: 'blog' })}</p>
                            )}
                        </div>
                    </div>
                </section>

                {relatedPosts.length > 0 && (
                    <section ref={relatedRef} className={`related-posts-section section-bg-light-gray animate-on-scroll ${isRelatedVisible ? 'is-visible' : ''}`}>
                        <div className="container">
                            <h3>{t('read_also', { ns: 'blog' })}</h3>
                            <div className="related-posts-grid">
                                {relatedPosts.map((relatedPost) => (
                                    <BlogPostCard key={relatedPost.id} post={relatedPost} />
                                ))}
                            </div>
                        </div>
                    </section>
                )}
            </article>
        </>
    );
}

export default BlogPostPage;